// Cross-platform OneSignal abstraction.
//
// Track A (web): react-onesignal Web SDK v16. Existing behavior preserved
// exactly — initOneSignal / registerUserForPush still call OneSignal.init /
// OneSignal.login on the Web SDK.
//
// Track B (iOS / Android native via Capacitor): onesignal-cordova-plugin
// v5.x. The native plugin is dynamically imported INSIDE the
// isNativePlatform() guard so it is never bundled into the web build and
// its module-level `window.plugins.OneSignal = ...` side effect never runs
// in browsers.
//
// Both tracks set external_id = Supabase user_id, which is what the edge
// functions target via include_aliases.external_id.
//
// ⚠️ ORDERING GUARANTEE (prod incident 10/06: external_id empty on EVERY
// subscription, all platforms): login() called before init() completes is
// silently dropped by both SDKs. App.tsx fires initOneSignal() (not awaited)
// and onAuthStateChange can deliver INITIAL_SESSION on the very next tick —
// registerUserForPush used to race ahead of init and the alias never
// attached. Every public entry point below now awaits ensureInitialized()
// before touching the SDK, and failures are surfaced to Sentry instead of
// being swallowed.

import OneSignalWeb from "react-onesignal";
import { Capacitor } from "@capacitor/core";
import { capacitorErrorMessage } from "@/lib/capacitorErrors";
import { subscriptionCapturePatch } from "@/lib/push/subscriptionCapturePatch";
import { Sentry } from "@/lib/sentry";
import { supabase } from "@/integrations/supabase/client";

/**
 * react-onesignal type wide enough to call .login(externalId).
 * The package's own types are not exhaustively published.
 */
type WebOneSignal = {
  init: (config: Record<string, unknown>) => Promise<void>;
  login: (externalId: string) => Promise<void>;
  Notifications?: {
    requestPermission: () => Promise<boolean>;
  };
  Slidedown?: {
    promptPush: (opts?: { force?: boolean }) => Promise<void>;
  };
  User?: {
    externalId?: string | null;
    PushSubscription?: {
      id?: string | null;
      optedIn?: boolean;
      optIn?: () => Promise<void>;
      addEventListener?: (
        event: "change",
        cb: (e: { current?: { id?: string | null; optedIn?: boolean } }) => void,
      ) => void;
    };
  };
};

/**
 * Subset of the onesignal-cordova-plugin v5 surface we actually use.
 * Keeps native code paths fully typed without pulling the plugin's types
 * into the web bundle.
 */
interface NativeOneSignal {
  initialize: (appId: string) => void;
  login: (externalId: string) => void;
  logout: () => void;
  Notifications: {
    hasPermission: () => boolean;
    requestPermission: (fallbackToSettings?: boolean) => Promise<boolean>;
  };
  User?: {
    getExternalId?: () => Promise<string | null>;
    pushSubscription?: {
      id?: string | null;
      optedIn?: boolean;
      optIn?: () => void;
      addEventListener?: (
        event: "change",
        cb: (e: { current?: { id?: string | null; optedIn?: boolean } }) => void,
      ) => void;
    };
  };
}

let nativePluginCache: NativeOneSignal | null = null;

async function loadNativePlugin(): Promise<NativeOneSignal | null> {
  if (nativePluginCache) return nativePluginCache;
  try {
    // Dynamic import — keeps onesignal-cordova-plugin out of the web bundle.
    // The plugin's module-level code touches `window.plugins`; harmless in
    // Capacitor WebView, but we still gate the import so it never executes
    // on plain web.
    const mod = await import("onesignal-cordova-plugin");
    const plugin = (mod as { default?: NativeOneSignal }).default ?? (mod as unknown as NativeOneSignal);
    nativePluginCache = plugin;
    return plugin;
  } catch (err) {
    // Native plugin missing on this platform (e.g. running native build
    // without the plugin installed). Fail soft — no push, no crash.
    console.warn("[oneSignal] native plugin load failed:", err);
    return null;
  }
}

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function captureToSentry(err: unknown, feature: string): void {
  try {
    // Normalizar para Error é certo (o Sentry quer instância), mas `String(err)`
    // num objeto simples da ponte vira "[object Object]" e o issue nasce sem
    // causa — a mesma perda que apagou o motivo do sync de saúde (issue #480).
    Sentry.captureException(
      err instanceof Error ? err : new Error(capacitorErrorMessage(err)),
      { tags: { feature } },
    );
  } catch {
    // Sentry not initialized (dev) — console line at the call site stands.
  }
}

// OneSignal App ID — PUBLIC identifier (ships in the client either way), so a
// hardcoded fallback is safe and intentional: it guarantees the SDK still
// initializes on builds where VITE_ONESIGNAL_APP_ID wasn't injected at build
// time (the root cause of 0 iOS subscribers — initOneSignal() was returning
// early). The env var still wins when present so the value can be overridden
// per environment without a code change.
const ONESIGNAL_APP_ID_FALLBACK = "2c6def5a-30e1-4bb5-8485-ef4c55e93e82";

// Single init promise — every SDK call funnels through this so login /
// permission / listeners can never race ahead of initialize().
let initPromise: Promise<void> | null = null;

async function doInit(): Promise<void> {
  const appId =
    (import.meta.env.VITE_ONESIGNAL_APP_ID as string | undefined) ||
    ONESIGNAL_APP_ID_FALLBACK;
  if (!appId) return;

  if (isNative()) {
    const plugin = await loadNativePlugin();
    if (!plugin) return;
    try {
      plugin.initialize(appId);
    } catch (err) {
      console.warn("[oneSignal] native initialize failed:", err);
      captureToSentry(err, "onesignal_init_failed_native");
    }
    return;
  }

  // Web — react-onesignal owns the SDK script load (index.html must NOT
  // carry the CDN <script>; see the comment there). init() here is
  // once-per-page by construction (singleton below).
  try {
    await (OneSignalWeb as unknown as WebOneSignal).init({
      appId,
      allowLocalhostAsSecureOrigin: true,
      serviceWorkerParam: { scope: "/onesignal/" },
      notifyButton: { enable: false } as unknown,
      promptOptions: {
        slidedown: {
          prompts: [
            {
              type: "push",
              autoPrompt: false,
              delay: { pageViews: 0, timeDelay: 0 },
            } as unknown,
          ],
        },
      },
    });
  } catch (err) {
    // "SDK already initialized" = the SDK is USABLE — treat as success.
    // (Seen when a second copy/init won the race; with the singleton +
    // single script load this should no longer occur, but stay tolerant.)
    const msg = String((err as Error)?.message ?? err).toLowerCase();
    if (msg.includes("already initialized")) {
      console.warn("[oneSignal] web init: SDK already initialized — continuing");
      return;
    }
    console.warn("[oneSignal] web init failed:", err);
    captureToSentry(err, "onesignal_init_failed_web");
    // Do NOT rethrow: callers proceed to login(), which has its own retry +
    // capture. Rethrowing used to combine with the reset below into an
    // infinite re-init cascade.
  }
}

// LGPD consent gate (Termly banner -> main.tsx). Push SDK sits in the
// banner's marketing/advertising category. Blocked = init deferred (initPromise
// untouched, so a later grant can still init exactly once); revoke AFTER init
// cannot de-initialize the SDK this session (global already-initialized flag,
// see initOneSignal note below) — the subscription stays until logout and new
// sessions simply never init.
let consentAllowed = true;
let blockedAttempt = false;
export function setOneSignalConsentAllowed(allowed: boolean): void {
  consentAllowed = allowed;
  if (allowed && blockedAttempt && !initPromise) {
    blockedAttempt = false;
    initOneSignal().catch(() => {});
  }
  if (!allowed && initPromise) {
    console.log("[oneSignal] consent revoked after init — SDK stays active this session (cannot de-initialize)");
  }
}

export const initOneSignal = (): Promise<void> => {
  if (!consentAllowed) {
    blockedAttempt = true;
    console.log("[oneSignal] init deferred — marketing consent not granted");
    return Promise.resolve();
  }
  if (!initPromise) {
    // ⚠️ NEVER reset this on failure. The web SDK keeps a GLOBAL
    // already-initialized flag: re-calling init() after any failed attempt
    // throws forever. The previous reset-on-reject turned one bad init into
    // the prod /auth/callback cascade ("SDK already initialized" ×26 — every
    // ensureInitialized() re-ran init against that flag) and login() never
    // completed, leaving subscriptions anonymous.
    initPromise = doInit();
  }
  return initPromise;
};

/** Await SDK readiness; safe to call from any entry point. */
async function ensureInitialized(): Promise<void> {
  try {
    await initOneSignal();
  } catch {
    // doInit no longer rejects, but keep the guard for the native path.
  }
}

const LOGIN_RETRY_DELAY_MS = 3000;

// ── Patch de captura do subscription id — regra ÚNICA dos TRÊS writers ──────
// (persistSubscriptionSnapshot e saveOptInToDb em hooks/useOneSignal.ts, e
// captureSubscriptionIdAtBoot abaixo).
//
// A regra MORA em src/lib/push/subscriptionCapturePatch.ts — pura, sem imports,
// com o ciclo de vida completo do id documentado lá. Saiu deste arquivo (task
// 6cad8b58) porque é um contrato de ESCRITA NO BANCO que precisa ser afirmável
// por teste, e afirmá-lo daqui exigiria carregar o SDK do OneSignal, o
// Capacitor, o Sentry e o client do Supabase. O pino que casa as chaves do
// patch com as colunas REAIS de user_profiles está em
// src/test/pushCapturePatchSchema.test.ts.
//
// Re-exportado para os call sites existentes seguirem importando de
// "@/lib/oneSignal" sem nenhuma mudança.
export { subscriptionCapturePatch };

// Boot-time capture of the push subscription id. The "change" listener only
// fires when the subscription MUTATES — a device whose subscription was
// minted in a previous session never emits it, so the column stayed NULL for
// every already-subscribed user. After a successful login, read the CURRENT
// snapshot and persist it when it's a real, opted-in id that differs from
// the stored value. Idempotent, never prompts, Sentry only on a real write
// failure.
async function captureSubscriptionIdAtBoot(userId: string): Promise<void> {
  try {
    let id: string | null = null;
    let optedIn = false;
    if (isNative()) {
      const plugin = await loadNativePlugin();
      const sub = plugin?.User?.pushSubscription;
      id = (sub?.id as string | null | undefined) ?? null;
      optedIn = sub?.optedIn === true;
    } else {
      const sub = (OneSignalWeb as unknown as WebOneSignal).User?.PushSubscription;
      id = sub?.id ?? null;
      optedIn = sub?.optedIn === true;
    }
    if (!id || !optedIn) return; // nothing real yet — the change listener covers late arrivals

    const { data, error } = await supabase
      .from("user_profiles")
      .select("onesignal_subscription_id, onesignal_subscription_dead_at")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      console.error("[oneSignal] subscription id read failed:", error);
      return; // read-side hiccup — retried on next boot
    }
    const row = data as { onesignal_subscription_id?: string | null; onesignal_subscription_dead_at?: string | null } | null;
    if (row?.onesignal_subscription_id === id && !row?.onesignal_subscription_dead_at) {
      return; // already captured & alive — no-op
    }
    // id novo OU id igual porém carimbado morto (revival com o MESMO id —
    // ex.: usuário religou notificações no SO e a subscription voltou a
    // subscribed sem trocar de id). O guard antigo dava no-op no segundo caso
    // e o carimbo nunca seria limpo — push morto pra sempre. O write de
    // captura (subscriptionCapturePatch) zera dead_at nos dois casos.
    const { error: upErr } = await supabase
      .from("user_profiles")
      .update(subscriptionCapturePatch(id) as never)
      .eq("user_id", userId);
    if (upErr) {
      console.warn("[oneSignal] subscription id boot save failed:", upErr);
      captureToSentry(upErr, "push_subscription_save_failed");
    }
  } catch (err) {
    console.error("[oneSignal] captureSubscriptionIdAtBoot threw:", err);
  }
}

export const registerUserForPush = async (userId: string, isCurrent?: () => boolean): Promise<void> => {
  const assert = () => { if (isCurrent && !isCurrent()) throw new Error("invalid_session"); };
  assert(); await ensureInitialized(); assert();

  // One attempt + one retry with backoff. login(user_id) is idempotent in
  // SDK v16 / plugin v5 (same external id = no-op), so calling this on every
  // authenticated boot is safe — that's the design: the alias must attach on
  // EVERY session start, not only on /auth/callback.
  const attempt = async (): Promise<void> => {
    if (isNative()) {
      const plugin = await loadNativePlugin(); assert();
      if (!plugin) return;
      assert(); plugin.login(userId);
      // Best-effort verification — getExternalId exists on plugin >= 5.1.
      // A mismatch here is exactly the "external_id empty on the dashboard"
      // prod symptom, so it must be VISIBLE, not assumed.
      const got = await plugin.User?.getExternalId?.().catch(() => null); assert();
      if (got !== undefined && got !== null && got !== userId) {
        throw new Error("native external_id mismatch: expected " + userId + " got " + String(got));
      }
      return;
    }
    assert(); await (OneSignalWeb as unknown as WebOneSignal).login(userId); assert();
    const got = (OneSignalWeb as unknown as WebOneSignal).User?.externalId;
    if (got !== undefined && got !== userId) {
      throw new Error("web external_id mismatch: expected " + userId + " got " + String(got));
    }
  };

  let loggedIn = false;
  try {
    await attempt();
    loggedIn = true;
  } catch (firstErr) {
    assert();
    console.warn("[oneSignal] login attempt 1 failed, retrying in 3s:", firstErr);
    await new Promise((r) => setTimeout(r, LOGIN_RETRY_DELAY_MS)); assert();
    try {
      await attempt();
      loggedIn = true;
    } catch (finalErr) {
      assert();
      console.warn("[oneSignal] login failed after retry:", finalErr);
      captureToSentry(finalErr, isNative() ? "onesignal_login_failed_native" : "onesignal_login_failed_web");
    }
  }

  // Subscription-id capture rides the successful boot login (fire-and-forget;
  // dispatchers prefer this id over the alias — see _shared/onesignal.ts).
  if (loggedIn && !isCurrent) {
    void captureSubscriptionIdAtBoot(userId);
  }
};

/**
 * Platform-aware push permission prompt. Returns true if the user is
 * subscribed afterwards. Used by useOneSignal's requestPermission path —
 * web still goes through the slidedown for the softer pre-prompt UX.
 */
export const requestNotificationPermission = async (isCurrent?: () => boolean): Promise<boolean> => {
  const assert = () => { if (isCurrent && !isCurrent()) throw new Error("invalid_session"); };
  assert(); await ensureInitialized(); assert();

  if (isNative()) {
    const plugin = await loadNativePlugin(); assert();
    if (!plugin) return false;
    try {
      const granted = await plugin.Notifications.requestPermission(true); assert(); return granted;
    } catch (err) {
      assert();
      console.warn("[oneSignal] native requestPermission failed:", err);
      captureToSentry(err, "onesignal_permission_failed_native");
      return false;
    }
  }

  try {
    const ok = await (OneSignalWeb as unknown as WebOneSignal).Notifications?.requestPermission(); assert();
    return ok === true;
  } catch (err) {
    assert();
    console.warn("[oneSignal] web requestPermission failed:", err);
    captureToSentry(err, "onesignal_permission_failed_web");
    return false;
  }
};

/**
 * Native (plugin v5): explicitly opt the push subscription in after the OS
 * permission is granted. Without this the dashboard can show the device as
 * "Never Subscribed / Never Prompted" even though iOS permission was
 * accepted — permission and subscription opt-in are separate states in v5.
 * No-op on web (the Web SDK opts in as part of the slidedown flow).
 */
export const optInPushSubscription = async (isCurrent?: () => boolean): Promise<void> => {
  if (!isNative()) return;
  const assert = () => { if (isCurrent && !isCurrent()) throw new Error("invalid_session"); };
  assert(); await ensureInitialized(); assert();
  const plugin = await loadNativePlugin(); assert();
  if (!plugin) return;
  try {
    plugin.User?.pushSubscription?.optIn?.();
  } catch (err) {
    assert();
    console.warn("[oneSignal] native pushSubscription.optIn failed:", err);
    captureToSentry(err, "onesignal_optin_failed_native");
  }
};

export interface PushSubscriptionSnapshot {
  id: string | null;
  optedIn: boolean;
}

/**
 * Subscribe to push-subscription changes on BOTH tracks. The subscription id
 * is created asynchronously AFTER permission (classic race: an immediate
 * read right after the prompt returns null — the prod cause of
 * onesignal_subscription_id NULL for 100% of users). This listener is the
 * reliable capture point; useOneSignal persists the snapshot to
 * user_profiles.
 */
export const onPushSubscriptionChange = async (
  cb: (snap: PushSubscriptionSnapshot) => void,
): Promise<void> => {
  await ensureInitialized();

  if (isNative()) {
    const plugin = await loadNativePlugin();
    if (!plugin) return;
    try {
      plugin.User?.pushSubscription?.addEventListener?.("change", (e) => {
        cb({ id: e?.current?.id ?? null, optedIn: e?.current?.optedIn === true });
      });
    } catch (err) {
      console.warn("[oneSignal] native change listener failed:", err);
      captureToSentry(err, "onesignal_listener_failed_native");
    }
    return;
  }

  try {
    (OneSignalWeb as unknown as WebOneSignal).User?.PushSubscription?.addEventListener?.(
      "change",
      (e) => {
        cb({ id: e?.current?.id ?? null, optedIn: e?.current?.optedIn === true });
      },
    );
  } catch (err) {
    console.warn("[oneSignal] web change listener failed:", err);
    captureToSentry(err, "onesignal_listener_failed_web");
  }
};
